import Api from "lib/api/httpRequestHelper";

const ENDPOINT = "franchiseMarketing";

function handleApiError(response) {
  if (response?.Success === false) {
    throw new Error(response?.ValidationMessage || "Erro na requisição.");
  }
  return response;
}

export async function getUnifiedList({
  aba = "Todos",
  status,
  busca,
  ordenacao = "recentes",
  pagina = 1,
  tamanho = 25,
}) {
  const params = new URLSearchParams({
    aba,
    ordenacao,
    pagina: String(pagina),
    tamanho: String(tamanho),
  });

  if (status) params.set("status", status);
  if (busca?.trim()) params.set("busca", busca.trim());

  const response = handleApiError(
    await Api.get(
      "franchise-marketing-list",
      ENDPOINT,
      `?${params.toString()}`,
      true,
    ),
  );

  return response?.Result || { Itens: [], Total: 0 };
}

export async function getStatusSummary() {
  const response = handleApiError(
    await Api.get("franchise-marketing-status-summary", ENDPOINT, "", true),
  );
  return response?.Result || {};
}

export async function listCategories(apenasAtivos = false) {
  const param = apenasAtivos ? "" : "?apenasAtivos=false";
  const response = handleApiError(
    await Api.get("franchise-marketing-category-list", ENDPOINT, param, true),
  );
  return response?.Result || [];
}

export async function createCategory(data) {
  const response = handleApiError(
    await Api.post("franchise-marketing-category-create", ENDPOINT, data, true),
  );
  return response?.Result;
}

export async function editCategory(data) {
  const response = handleApiError(
    await Api.put("franchise-marketing-category-edit", ENDPOINT, data, true),
  );
  return response?.Result;
}

export async function deleteCategory(id) {
  const response = handleApiError(
    await Api.delete(
      "franchise-marketing-category-delete",
      ENDPOINT,
      { idFranquiaMarketingCategoria: id },
      true,
    ),
  );
  return response?.Result;
}

export async function listCampaigns(params = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.busca) qs.set("busca", params.busca);
  const param = qs.toString() ? `?${qs.toString()}` : "";
  const response = handleApiError(
    await Api.get("franchise-marketing-campaign-list", ENDPOINT, param, true),
  );
  return response?.Result || [];
}

export async function listContents(params = {}) {
  const qs = new URLSearchParams();
  if (params.tipo) qs.set("tipo", params.tipo);
  if (params.status) qs.set("status", params.status);
  if (params.busca) qs.set("busca", params.busca);
  if (params.idFranquiaMarketingCategoria) {
    qs.set("idFranquiaMarketingCategoria", params.idFranquiaMarketingCategoria);
  }
  const param = qs.toString() ? `?${qs.toString()}` : "";
  const response = handleApiError(
    await Api.get("franchise-marketing-content-list", ENDPOINT, param, true),
  );
  return response?.Result || [];
}

export async function getCampaignById(id) {
  const campaigns = await listCampaigns();
  return campaigns.find((c) => c.IdFranquiaMarketingCampanha === id) || null;
}

export async function getContentById(id) {
  const contents = await listContents();
  return contents.find((c) => c.IdFranquiaMarketingConteudo === id) || null;
}

export async function loadFullEntity(row) {
  if (row.ItemTipo === "Campanha") {
    return getCampaignById(row.Id);
  }
  return getContentById(row.Id);
}

export function formatDateForApi(value) {
  if (value == null || value === "") return value;
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  const brMatch = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (brMatch) {
    return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
  }

  return str;
}

function normalizeCampaignPayload(body = {}) {
  return {
    ...body,
    ...(body.dataInicio != null && {
      dataInicio: formatDateForApi(body.dataInicio),
    }),
    ...(body.dataFim != null && {
      dataFim: formatDateForApi(body.dataFim),
    }),
  };
}

function normalizeContentPayload(body = {}) {
  return {
    ...body,
    ...(body.dataInicioVigencia != null && {
      dataInicioVigencia: formatDateForApi(body.dataInicioVigencia),
    }),
    ...(body.dataFimVigencia != null && {
      dataFimVigencia: formatDateForApi(body.dataFimVigencia),
    }),
  };
}

export async function createCampaign(body) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-campaign-create",
      ENDPOINT,
      normalizeCampaignPayload(body),
      true,
    ),
  );
  return response?.Result;
}

export async function editCampaign(body) {
  const response = handleApiError(
    await Api.put(
      "franchise-marketing-campaign-edit",
      ENDPOINT,
      normalizeCampaignPayload(body),
      true,
    ),
  );
  return response?.Result;
}

export async function createContent(body) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-content-create",
      ENDPOINT,
      normalizeContentPayload(body),
      true,
    ),
  );
  return response?.Result;
}

export async function editContent(body) {
  const response = handleApiError(
    await Api.put(
      "franchise-marketing-content-edit",
      ENDPOINT,
      normalizeContentPayload(body),
      true,
    ),
  );
  return response?.Result;
}

export async function changeCampaignStatus(id, status, extra = {}) {
  const response = handleApiError(
    await Api.put(
      "franchise-marketing-campaign-status",
      ENDPOINT,
      { idFranquiaMarketingCampanha: id, status, ...extra },
      true,
    ),
  );
  return response?.Result;
}

export async function changeContentStatus(id, status, extra = {}) {
  const response = handleApiError(
    await Api.put(
      "franchise-marketing-content-status",
      ENDPOINT,
      { idFranquiaMarketingConteudo: id, status, ...extra },
      true,
    ),
  );
  return response?.Result;
}

export async function deleteCampaign(id) {
  const response = handleApiError(
    await Api.delete(
      "franchise-marketing-campaign-delete",
      ENDPOINT,
      { idFranquiaMarketingCampanha: id },
      true,
    ),
  );
  return response?.Result;
}

export async function deleteContent(id) {
  const response = handleApiError(
    await Api.delete(
      "franchise-marketing-content-delete",
      ENDPOINT,
      { idFranquiaMarketingConteudo: id },
      true,
    ),
  );
  return response?.Result;
}

export async function duplicateContent(id) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-content-duplicate",
      ENDPOINT,
      { idFranquiaMarketingConteudo: id },
      true,
    ),
  );
  return response?.Result;
}

export async function shareCampaign(id) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-campaign-share",
      ENDPOINT,
      { idFranquiaMarketingCampanha: id },
      true,
    ),
  );
  return response?.Result;
}

export async function shareContent(id) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-content-share",
      ENDPOINT,
      { idFranquiaMarketingConteudo: id },
      true,
    ),
  );
  return response?.Result;
}

export async function listHistory(entidade, id) {
  const params = new URLSearchParams({ entidade, id: String(id) });
  const response = handleApiError(
    await Api.get(
      "franchise-marketing-history-list",
      ENDPOINT,
      `?${params.toString()}`,
      true,
    ),
  );
  return response?.Result || [];
}

export async function listPlaylists(apenasAtivos = true) {
  const param = apenasAtivos ? "" : "?apenasAtivos=false";
  const response = handleApiError(
    await Api.get("franchise-marketing-playlist-list", ENDPOINT, param, true),
  );
  return response?.Result || [];
}

export async function createPlaylist(data) {
  const response = handleApiError(
    await Api.post("franchise-marketing-playlist-create", ENDPOINT, data, true),
  );
  return response?.Result;
}

export async function editPlaylist(data) {
  const response = handleApiError(
    await Api.put("franchise-marketing-playlist-edit", ENDPOINT, data, true),
  );
  return response?.Result;
}

export async function addPlaylistItem(idPlaylist, idConteudo) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-playlist-item-add",
      ENDPOINT,
      { idFranquiaMarketingPlaylist: idPlaylist, idFranquiaMarketingConteudo: idConteudo },
      true,
    ),
  );
  return response?.Result;
}

export async function removePlaylistItem(idItem) {
  const response = handleApiError(
    await Api.delete(
      "franchise-marketing-playlist-item-remove",
      ENDPOINT,
      { idFranquiaMarketingPlaylistItem: idItem },
      true,
    ),
  );
  return response?.Result;
}

function resolveImagemExibicao(tipo, arquivoUrl, thumbnailUrl) {
  if (["Banner", "Lookbook", "Campanha"].includes(tipo)) {
    return arquivoUrl || thumbnailUrl || "";
  }
  if (tipo === "Video") return thumbnailUrl || "";
  return thumbnailUrl || arquivoUrl || "";
}

function mapPortalHeroFromApi(item) {
  return {
    id: item.Id,
    titulo: item.Titulo,
    subtitulo: item.Subtitulo || "",
    imagem: item.Imagem || "",
    campanhaId: item.CampanhaId,
  };
}

function mapPortalItemFromApi(item) {
  return {
    id: item.Id,
    itemTipo: item.ItemTipo,
    titulo: item.Titulo,
    descricao: item.Descricao || "",
    tipo: item.Tipo,
    categoria: item.Categoria || "",
    campanha: item.Campanha || "",
    tags: item.Tags || [],
    status: item.Status,
    dataInicio: item.DataInicio || "",
    dataFim: item.DataFim || "",
    dataAtualizacao: item.DataAtualizacao,
    downloads: item.Downloads || 0,
    thumbnail: item.Thumbnail || "",
    arquivoUrl: item.ArquivoUrl || "",
    imagemExibicao: item.ImagemExibicao || "",
    videoUrl: item.VideoUrl || "",
    isNovo: !!item.IsNovo,
    isCampaign: !!item.IsCampaign,
  };
}

export async function getPortalList() {
  const response = handleApiError(
    await Api.get("franchise-marketing-portal-list", ENDPOINT, "", true),
  );
  const result = response?.Result || {};
  return {
    heroCampaigns: (result.HeroCampaigns || []).map(mapPortalHeroFromApi),
    portalContents: (result.PortalContents || []).map(mapPortalItemFromApi),
  };
}

export async function listFavorites() {
  const response = handleApiError(
    await Api.get("franchise-marketing-favorite-list", ENDPOINT, "", true),
  );
  return response?.Result || [];
}

export async function toggleFavorite(entidadeTipo, entidadeId) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-favorite-toggle",
      ENDPOINT,
      { entidadeTipo, entidadeId },
      true,
    ),
  );
  return response?.Result;
}

export async function getPublishedPortalData() {
  try {
    return await getPortalList();
  } catch {
    const [campaigns, contents] = await Promise.all([
      listCampaigns({ status: "Publicado" }),
      listContents({ status: "Publicado" }),
    ]);

    const campaignMap = campaigns.reduce((acc, c) => {
      acc[c.IdFranquiaMarketingCampanha] = c.Nome;
      return acc;
    }, {});

    const heroCampaigns = campaigns
      .filter((c) => c.DestaqueHome)
      .map((c) => ({
        id: c.IdFranquiaMarketingCampanha,
        titulo: c.Nome,
        subtitulo: c.Descricao || "",
        imagem: resolveImagemExibicao(
          "Campanha",
          c.UrlBannerPrincipal,
          c.UrlThumbnail,
        ),
        campanhaId: c.IdFranquiaMarketingCampanha,
      }));

    const portalCampaigns = campaigns.map((c) => ({
      id: c.IdFranquiaMarketingCampanha,
      itemTipo: "Campanha",
      titulo: c.Nome,
      descricao: c.Descricao || "",
      tipo: "Campanha",
      categoria: c.NomeCategoria || "",
      campanha: c.Nome,
      tags: c.Tags || [],
      status: c.Status,
      dataInicio: formatDateOnly(c.DataInicio),
      dataFim: formatDateOnly(c.DataFim),
      dataAtualizacao: c.DataAtualizacao || c.DataInclusao,
      downloads: c.QuantidadeAcessos || 0,
      thumbnail: c.UrlThumbnail || "",
      videoUrl: "",
      arquivoUrl: c.UrlBannerPrincipal || "",
      imagemExibicao: resolveImagemExibicao(
        "Campanha",
        c.UrlBannerPrincipal,
        c.UrlThumbnail,
      ),
      isNovo: isRecentDate(c.DataAtualizacao || c.DataInclusao),
      isCampaign: true,
    }));

    const portalContents = [
      ...portalCampaigns,
      ...contents.map((c) => ({
        id: c.IdFranquiaMarketingConteudo,
        itemTipo: "Conteudo",
        titulo: c.Titulo,
        descricao: c.Descricao || "",
        tipo: c.Tipo,
        categoria: c.NomeCategoria || "",
        campanha: campaignMap[c.IdFranquiaMarketingCampanha] || "",
        tags: c.Tags || [],
        status: c.Status,
        dataInicio: formatDateOnly(c.DataInicioVigencia),
        dataFim: formatDateOnly(c.DataFimVigencia),
        dataAtualizacao: c.DataAtualizacao || c.DataInclusao,
        downloads: c.QuantidadeAcessos || 0,
        thumbnail: c.UrlThumbnail || "",
        videoUrl: c.UrlExterna || c.UrlArquivo || "",
        arquivoUrl: c.UrlArquivo || "",
        imagemExibicao: resolveImagemExibicao(
          c.Tipo,
          c.UrlArquivo,
          c.UrlThumbnail,
        ),
        isNovo: isRecentDate(c.DataAtualizacao || c.DataInclusao),
        isCampaign: false,
      })),
    ];

    return { heroCampaigns, portalContents };
  }
}

function formatDateOnly(value) {
  if (!value) return "";
  const str = String(value);
  return str.includes("T") ? str.split("T")[0] : str;
}

function isRecentDate(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  return diff < 7 * 24 * 60 * 60 * 1000;
}

function getFileExtension(file) {
  return file?.name?.split(".")?.pop()?.toLowerCase() || "jpg";
}

const URL_FIELD_MAP = {
  thumbnail: "urlThumbnail",
  arquivo: "urlArquivo",
  bannerPrincipal: "urlBannerPrincipal",
};

async function getMarketingUploadUrl({ titulo, extensao }) {
  const response = handleApiError(
    await Api.post(
      "franchise-marketing-upload-url",
      ENDPOINT,
      { titulo, extensao },
      true,
    ),
  );
  return response?.Result;
}

async function uploadFileToPresignedUrl(uploadUrl, file, contentType) {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: await file.arrayBuffer(),
    headers: {
      "Content-Type": contentType || "application/octet-stream",
    },
  });

  if (!response.ok) {
    throw new Error(
      "Falha ao enviar arquivo para o S3. Verifique se o bucket possui CORS habilitado para o domínio do portal.",
    );
  }
}

export async function uploadMarketingFile(files, fieldName, titulo = "upload") {
  const file = files?.[0];
  if (!file) return {};

  const extension = getFileExtension(file);
  const urlField = URL_FIELD_MAP[fieldName] || `url${fieldName}`;
  const uploadInfo = await getMarketingUploadUrl({
    titulo: titulo?.trim() || "upload",
    extensao: extension,
  });

  await uploadFileToPresignedUrl(
    uploadInfo.uploadUrl,
    file,
    uploadInfo.contentType,
  );

  return { [urlField]: uploadInfo.fileUrl };
}

export async function uploadMarketingFiles(fileFields = []) {
  const payloads = await Promise.all(
    fileFields.map(({ files, fieldName, titulo }) =>
      uploadMarketingFile(files, fieldName, titulo),
    ),
  );
  return Object.assign({}, ...payloads);
}

export function buildSegmentacao(segmentacao) {
  if (!segmentacao) return [];
  const items = Array.isArray(segmentacao) ? segmentacao : [segmentacao];
  return items
    .filter(Boolean)
    .map((item) => ({
      tipoSegmentacao: item.value || item.tipoSegmentacao,
      idReferencia: item.idReferencia || null,
    }));
}

export function buildTags(tags) {
  if (!tags?.trim()) return [];
  return tags
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export function formatShareMessage(result) {
  const token = result?.TokenCompartilhamento;
  if (!token) return null;
  return String(token);
}
